/**
 * Shareable accomplishment images — rendered with the Canvas 2D API.
 *
 * We draw to a fixed 1080×1920 (9:16 "stories") canvas rather than capturing the
 * DOM: it gives deterministic, high-resolution output that renders identically on
 * the web and inside the Capacitor Android webview, with no html-to-image font or
 * oklch pitfalls.
 *
 * Colours are sRGB hex equivalents of the app's oklch design tokens
 * (see packages/ui/src/index.css) so they render regardless of a webview's
 * canvas-oklch support. The token each constant mirrors is noted inline.
 */

export type ShareCardType = 'completion' | 'streak';
export type ShareCardTheme = 'light' | 'dark';
export type ShareVariant = `${ShareCardType}-${ShareCardTheme}`;

export interface ShareData {
  earned: number;
  max: number;
  done: number;
  streak: number;
  date: string; // YYYY-MM-DD
}

const W = 1080;
const H = 1920;

interface Theme {
  bg: [string, string, string]; // vertical background gradient stops
  glowTint: string; // warm corner glow
  text: string;
  textMuted: string;
  ringTrack: string;
  ringStroke: string; // ring arc / flame accent
  bigNumber: string; // ring centre + streak number — high contrast
  accentText: string; // celebratory taglines
  flame: string;
  flameBody: string; // branded streak-flame outer body
  flameCore: string; // branded streak-flame inner core
  wordmark: [string, string, string]; // wordmark gradient stops
  ringGlow: string;
}

const PALETTE: Record<ShareCardTheme, Theme> = {
  light: {
    // Warm sunset gradient — keeps colour presence across the whole card
    // rather than being mostly cream.
    bg: ['#fff2dc', '#fbd79e', '#f1b063'],
    glowTint: 'rgba(176,65,0,0.16)', // terracotta glow (--primary)
    text: '#2a1c11', // --text
    textMuted: '#6b4a2e', // darker warm muted for contrast on the amber bg
    ringTrack: 'rgba(42,28,17,0.12)',
    ringStroke: '#d9981f', // deepened gold so the ring reads on amber
    bigNumber: '#8f3200', // deep terracotta — strong contrast
    accentText: '#b04100', // --primary terracotta
    flame: '#e24a00', // --flame
    // Faithful to the branded ic_flame: espresso body + amber core, crisp on amber.
    flameBody: '#3a2415',
    flameCore: '#d98a2b',
    wordmark: ['#e49324', '#c14a00', '#942f00'], // .title-italics terracotta
    ringGlow: 'rgba(217,152,31,0.5)',
  },
  dark: {
    bg: ['#1b0f07', '#241307', '#321a0a'], // espresso, subtly warming downward
    glowTint: 'rgba(223,145,26,0.35)',
    text: '#f2eade', // --text (dark)
    textMuted: '#b7a897', // --text-muted (dark)
    ringTrack: 'rgba(255,255,255,0.14)',
    ringStroke: '#f0bb3b', // --gold (dark)
    bigNumber: '#f0bb3b', // --gold (dark)
    accentText: '#ffb060', // warm amber on espresso
    flame: '#fc6d00', // --flame (dark)
    // Adapted for espresso bg: warm body so the mark glows, brighter amber core.
    flameBody: '#e8650f',
    flameCore: '#ffd36b',
    wordmark: ['#f0bb3b', '#e0892a', '#db7213'], // brighter gold→amber for espresso
    ringGlow: 'rgba(240,187,59,0.6)',
  },
};

const DISPLAY = "'Bricolage Grotesque', system-ui, sans-serif";
const BODY = "'Plus Jakarta Sans', system-ui, sans-serif";

// ---- font + icon loading -------------------------------------------------

let fontsReady: Promise<void> | null = null;
function ensureFonts(): Promise<void> {
  if (!fontsReady) {
    fontsReady = (async () => {
      try {
        await Promise.all([
          document.fonts.load("800 100px 'Bricolage Grotesque'"),
          document.fonts.load("700 100px 'Bricolage Grotesque'"),
          document.fonts.load("600 40px 'Plus Jakarta Sans'"),
          document.fonts.load("500 40px 'Plus Jakarta Sans'"),
        ]);
        await document.fonts.ready;
      } catch {
        /* fall back to system fonts if loading fails */
      }
    })();
  }
  return fontsReady;
}

let iconPromise: Promise<HTMLImageElement | null> | null = null;
function loadIcon(): Promise<HTMLImageElement | null> {
  if (!iconPromise) {
    iconPromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = '/favicon.svg';
    });
  }
  return iconPromise;
}

// ---- drawing helpers -----------------------------------------------------

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawBackground(ctx: CanvasRenderingContext2D, t: Theme) {
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, t.bg[0]);
  grad.addColorStop(0.5, t.bg[1]);
  grad.addColorStop(1, t.bg[2]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Warm radial glow from the top-right, echoing .hero-card::after
  const glow = ctx.createRadialGradient(W * 0.85, -120, 0, W * 0.85, -120, W * 1.1);
  glow.addColorStop(0, t.glowTint);
  glow.addColorStop(0.55, 'rgba(0,0,0,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
}

/** Centred Chortle logo lockup: app-icon glyph + wordmark. */
async function drawLogo(ctx: CanvasRenderingContext2D, t: Theme, cy: number) {
  const icon = await loadIcon();
  const size = 88;
  const gap = 22;
  ctx.font = `800 76px ${DISPLAY}`;
  const wordW = ctx.measureText('Chortle').width;
  const totalW = size + gap + wordW;
  const startX = (W - totalW) / 2;

  if (icon) {
    ctx.save();
    roundRect(ctx, startX, cy - size / 2, size, size, 22);
    ctx.clip();
    ctx.drawImage(icon, startX, cy - size / 2, size, size);
    ctx.restore();
  }

  const wordX = startX + (icon ? size + gap : 0);
  const grad = ctx.createLinearGradient(wordX, cy - 40, wordX + wordW, cy + 40);
  grad.addColorStop(0, t.wordmark[0]);
  grad.addColorStop(0.5, t.wordmark[1]);
  grad.addColorStop(1, t.wordmark[2]);
  ctx.fillStyle = grad;
  ctx.font = `800 76px ${DISPLAY}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('Chortle', wordX, cy + 4);
}

function drawEyebrow(ctx: CanvasRenderingContext2D, t: Theme, text: string, cy: number) {
  ctx.fillStyle = t.textMuted;
  ctx.font = `700 30px ${BODY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.save();
  // letter-spacing emulation
  const letters = text.split('');
  const spacing = 6;
  const widths = letters.map((l) => ctx.measureText(l).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * (letters.length - 1);
  let x = (W - total) / 2;
  ctx.textAlign = 'left';
  for (let i = 0; i < letters.length; i++) {
    ctx.fillText(letters[i]!, x, cy);
    x += widths[i]! + spacing;
  }
  ctx.restore();
}

/** Points ring — mirrors PointsWidget's CircularProgress at goal-complete. */
function drawRing(ctx: CanvasRenderingContext2D, t: Theme, cx: number, cy: number, earned: number) {
  const radius = 300;
  const stroke = 46;
  const start = -Math.PI / 2;

  // track
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.strokeStyle = t.ringTrack;
  ctx.lineWidth = stroke;
  ctx.stroke();

  // full progress arc (goal hit) with a gold glow
  ctx.save();
  ctx.shadowColor = t.ringGlow;
  ctx.shadowBlur = 40;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, start, start + Math.PI * 2);
  ctx.strokeStyle = t.ringStroke;
  ctx.lineWidth = stroke;
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.restore();

  // centre number + label
  ctx.fillStyle = t.bigNumber;
  ctx.font = `800 200px ${DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(String(earned), cx, cy + 36);
  ctx.fillStyle = t.textMuted;
  ctx.font = `700 48px ${BODY}`;
  ctx.fillText('pts', cx, cy + 130);
}

// Branded streak flame — a rounder, friendlier take on android's ic_flame
// (24×24 viewport, pure cubics). Two-tone: outer body + inner core.
const FLAME_BODY = new Path2D(
  'M12,2.4 C12.6,5.4 14.6,6.8 16.2,9 C17.4,10.7 18,12.7 18,14.8 C18,18.6 15.3,21 12,21 C8.7,21 6,18.6 6,14.8 C6,11.5 8.2,9.4 9.6,6.6 C10.4,5 11.6,4 12,2.4 Z',
);
const FLAME_CORE = new Path2D(
  'M12,12 C13.4,13.6 14.2,14.8 14.2,16.4 C14.2,18.1 13,19.2 11.6,19.2 C10.3,19.2 9.3,18.2 9.3,16.8 C9.3,15.2 10.8,14.2 12,12 Z',
);

/** Draws the branded streak flame centred at (cx, cy), `size` px tall. */
function drawFlame(ctx: CanvasRenderingContext2D, t: Theme, cx: number, cy: number, size: number) {
  const scale = size / 24;
  ctx.save();
  ctx.translate(cx - 12 * scale, cy - 12 * scale);
  ctx.scale(scale, scale);
  ctx.save();
  ctx.shadowColor = t.ringGlow;
  ctx.shadowBlur = 24 / scale;
  ctx.fillStyle = t.flameBody;
  ctx.fill(FLAME_BODY);
  ctx.restore();
  ctx.fillStyle = t.flameCore;
  ctx.fill(FLAME_CORE);
  ctx.restore();
}

function drawHeadline(ctx: CanvasRenderingContext2D, color: string, text: string, cy: number, size = 92) {
  ctx.fillStyle = color;
  ctx.font = `800 ${size}px ${DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, W / 2, cy);
}

function drawSubtext(ctx: CanvasRenderingContext2D, color: string, text: string, cy: number, size = 44) {
  ctx.fillStyle = color;
  ctx.font = `500 ${size}px ${BODY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, W / 2, cy);
}

function formatDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

// ---- card compositions ---------------------------------------------------

async function drawCompletion(ctx: CanvasRenderingContext2D, t: Theme, data: ShareData) {
  drawBackground(ctx, t);
  await drawLogo(ctx, t, 210);
  drawEyebrow(ctx, t, 'TODAY', 360);
  drawRing(ctx, t, W / 2, 760, data.earned);
  drawHeadline(ctx, t.text, 'Day done!', 1230);
  const chores = `${data.done} chore${data.done === 1 ? '' : 's'} done today`;
  drawSubtext(ctx, t.textMuted, chores, 1320);
  drawSubtext(ctx, t.accentText, 'Every job cleared — nice work. ✨', 1400, 40);
  drawSubtext(ctx, t.textMuted, formatDate(data.date), 1790, 34);
}

async function drawStreak(ctx: CanvasRenderingContext2D, t: Theme, data: ShareData) {
  drawBackground(ctx, t);
  await drawLogo(ctx, t, 210);
  drawEyebrow(ctx, t, 'ON A STREAK', 380);

  // branded streak flame
  drawFlame(ctx, t, W / 2, 700, 340);

  // big streak number
  ctx.fillStyle = t.bigNumber;
  ctx.font = `800 360px ${DISPLAY}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(String(data.streak), W / 2, 1180);

  drawHeadline(ctx, t.text, `${data.streak}-day streak`, 1340, 84);
  drawSubtext(ctx, t.textMuted, 'Keeping it going, day after day.', 1430);
  drawSubtext(ctx, t.flame, "You're on a roll!", 1510, 40);
  drawSubtext(ctx, t.textMuted, formatDate(data.date), 1790, 34);
}

// ---- public API ----------------------------------------------------------

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
}

export async function renderShareCard(
  variant: ShareVariant,
  data: ShareData,
): Promise<{ variant: ShareVariant; blob: Blob; dataUrl: string }> {
  await ensureFonts();
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unsupported');

  const [type, theme] = variant.split('-') as [ShareCardType, ShareCardTheme];
  const t = PALETTE[theme];
  if (type === 'streak') await drawStreak(ctx, t, data);
  else await drawCompletion(ctx, t, data);

  const blob = await toBlob(canvas);
  return { variant, blob, dataUrl: canvas.toDataURL('image/png') };
}

/** The app's currently-active theme — mirrors the CSS rule in index.css
 *  (`prefers-color-scheme: dark` unless a `data-theme="light"` override is set). */
function activeTheme(): ShareCardTheme {
  if (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light') {
    return 'light';
  }
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * The variants to offer for a given day, in carousel order. The user's active
 * theme leads (a dark-mode user sees the dark cards first), so the first card
 * matches what they see in-app.
 */
export function shareVariantsFor(data: ShareData): ShareVariant[] {
  const primary = activeTheme();
  const secondary: ShareCardTheme = primary === 'dark' ? 'light' : 'dark';
  const types: ShareCardType[] = ['completion'];
  if (data.streak > 1) types.push('streak');
  return types.flatMap((t) => [`${t}-${primary}`, `${t}-${secondary}`] as ShareVariant[]);
}
